/**
 * **New work order** — `mockups/studio/client-detail.html`'s primary topbar button.
 *
 * A dialog holding `QuoteBuilder`, which is the component `.docs/20` §C names for it:
 * `work_orders_draft` + `work_orders_price` + `work_orders_propose$`. The dialog owns
 * the open state and the template list; the builder owns the three calls and the
 * confirmation. `ActionDialog` is not used because `work_orders_draft` on its own is
 * not what this button does — a draft nobody priced is not a quote.
 *
 * **The tier and the currency are the account's, not the retainer's.**
 * `StudioClientAccountSchema` carries `tier` and `currency` for every client and
 * neither is nullable — `currency` is documented there as "what they are billed in,
 * whatever a retainer says" — so there is no client this page cannot price. They used
 * to be read off the live retainer, which disabled this button for every prospect and
 * every project-only client: precisely the client a studio most wants to quote.
 *
 * **An archived account is the one disabled case that is left, and the button says
 * why.** `StudioClientAccountSchema.archived_at`: "an archived client is read-only:
 * there is nothing to view as and nothing to quote." The page's own banner is the
 * visible half of that sentence and the accessible name is this half, because a
 * disabled control that explains nothing is worse than no control.
 */

import { useState } from 'react'

import { ProjectTemplateSchema, type Currency, type TierCode } from '@fareast/shared'
import { useNavigate } from '@tanstack/react-router'
import { z } from 'zod'

import { useStudioRead } from '../../../api/studio.js'
import { Button } from '../../../components/portal/Button.js'
import { Dialog } from '../../../components/portal/Overlay.js'
import { Notice } from '../../../components/portal/Status.js'
import { QuoteBuilder } from '../../../components/studio/QuoteBuilder.js'

/**
 * What `catalog_list_project_templates` answers with.
 *
 * Parsed rather than cast: the RPC types come out of Hono's serialisation and a
 * `Money` or a `DateOnly` that arrived as something else would be a wrong price on a
 * quote rather than a red type. `ProjectTemplateSchema` is the catalog's own — no
 * second shape is written here.
 */
const TemplatesSchema = z.object({ templates: z.array(ProjectTemplateSchema) })

export type NewWorkOrderButtonProps = {
  readonly client: string
  /** `account.tier` — the client's own, never the retainer's snapshot. */
  readonly tier: TierCode
  /** `account.currency` — what they are billed in, whatever a retainer says. */
  readonly currency: Currency
  readonly retainerClient: boolean
  /** `account.archived_at !== null`. A closed account has nothing to quote. */
  readonly archived: boolean
}

export function NewWorkOrderButton({
  client,
  tier,
  currency,
  retainerClient,
  archived,
}: NewWorkOrderButtonProps) {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()

  const catalog = useStudioRead(
    'catalog_list_project_templates',
    {},
    { enabled: open, inline: true },
  )

  if (archived) {
    return (
      <Button
        variant="primary"
        disabled
        aria-label="New work order — this account is archived, so there is nothing to quote"
      >
        New work order
      </Button>
    )
  }

  const templates = catalog.data === undefined ? [] : TemplatesSchema.parse(catalog.data).templates

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      title="New work order"
      trigger={<Button variant="primary">New work order</Button>}
    >
      {catalog.isError ? (
        <Notice need role="alert">
          We could not read the project templates, so a quote cannot be composed.
        </Notice>
      ) : (
        <QuoteBuilder
          client={client}
          templates={templates}
          tier={tier}
          currency={currency}
          retainerClient={retainerClient}
          onDone={(ref) => {
            /*
             * The dialog closes and the reader lands on the quote they just sent.
             *
             * R2-25: raising something and being returned to the page you started on
             * confirms nothing — the ledger's counters move and the reference is
             * nowhere. A work order has a page of its own now, so the strongest
             * confirmation available is the thing itself: its reference, its state,
             * the schedule that went with it, and every next move on it.
             */
            setOpen(false)
            void navigate({ to: '/studio/work-orders/$ref', params: { ref } })
          }}
        />
      )}
    </Dialog>
  )
}
